import { useCallback, useEffect, useMemo } from 'react';
import { useGetCustomer } from '@/customers/application/adapters/getCustomer';
import { useGetCustomerFormOptions } from '@/customers/application/adapters/getCustomerFormOptions';
import { useSnackbar } from '@/common/presentation/SnackbarProvider';
import { OptionType } from '@/common/presentation/components/forms/fields/FormikAutocomplete';

export function useCustomerFormData(id?: number, skipQuery?: boolean) {
  const isNew = !id;
  const { showSnackbar } = useSnackbar();

  // Fetch existing customer data when editing
  const {
    customer,
    customerDetails,
    loading: customerLoading,
    error: customerError,
    availableCustomerTypes,
    availableSalesAgents,
    availableTerms,
    availablePricingSchemes,
    availableSalesRegions,
    availableAccountTypes
  } = useGetCustomer(Number(id), skipQuery);

  // Fetch form options when creating a new customer
  const {
    loading: formOptionsLoading,
    error: formOptionsError,
    availableCustomerTypes: formOptionsCustomerTypes,
    availableSalesAgents: formOptionsSalesAgents,
    availableTerms: formOptionsTerms,
    availablePricingSchemes: formOptionsPricingSchemes,
    availableSalesRegions: formOptionsSalesRegions,
    availableAccountTypes: formOptionsAccountTypes
  } = useGetCustomerFormOptions(!isNew || (skipQuery ?? false));

  useEffect(() => {
    if (customerError) {
      showSnackbar('Error', customerError.message || 'Failed to fetch customer data', 'error');
    }
    if (formOptionsError) {
      showSnackbar('Error', formOptionsError.message || 'Failed to fetch form options', 'error');
    }
  }, [customerError, formOptionsError, showSnackbar]);

  const resolvedAvailableSalesAgents = isNew ? (formOptionsSalesAgents ?? []) : (availableSalesAgents ?? []);

  // Transform availableSalesAgents to OptionType format for autocomplete
  const salesAgentOptions = useMemo<OptionType[]>(() => {
    return resolvedAvailableSalesAgents.map((agent) => ({
      value: agent.empId ?? -1,
      label: agent.fullName ?? 'Unknown Sales Agent',
      key: `sales-agent-${agent.empId}`
    }));
  }, [resolvedAvailableSalesAgents]);

  const defaultSalesAgentOption = useMemo<OptionType | undefined>(() => {
    const defaultAgent = resolvedAvailableSalesAgents.find((agent) => {
      const withDefault = agent as { default?: boolean | null };
      return Boolean(withDefault.default) && typeof agent.empId === 'number';
    });
    if (!defaultAgent) return undefined;
    return {
      value: defaultAgent.empId,
      label: defaultAgent.fullName ?? 'Unknown Sales Agent',
      key: `sales-agent-${defaultAgent.empId}`
    };
  }, [resolvedAvailableSalesAgents]);

  // Sales Agent loading state (always false since we're using static data)
  const salesAgentLoading = false;

  // Filter function for Sales Agent autocomplete
  const salesAgentFilterOptions = useCallback((options: OptionType[], { inputValue }: { inputValue: string }) => {
    const searchLower = inputValue.toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(searchLower));
  }, []);

  return {
    isNew,
    customer,
    customerDetails,
    customerLoading,
    customerError,
    availableCustomerTypes: isNew ? (formOptionsCustomerTypes ?? []) : (availableCustomerTypes ?? []),
    availableSalesAgents: resolvedAvailableSalesAgents,
    salesAgentOptions,
    defaultSalesAgentOption,
    salesAgentLoading,
    salesAgentFilterOptions,
    availableTerms: isNew ? (formOptionsTerms ?? []) : (availableTerms ?? []),
    availablePricingSchemes: isNew ? (formOptionsPricingSchemes ?? []) : (availablePricingSchemes ?? []),
    availableSalesRegions: isNew ? (formOptionsSalesRegions ?? []) : (availableSalesRegions ?? []),
    availableAccountTypes: isNew ? (formOptionsAccountTypes ?? []) : (availableAccountTypes ?? []),
    formOptionsLoading,
    formOptionsError,
    formOptionsCustomerTypes: formOptionsCustomerTypes ?? [],
    formOptionsSalesAgents: formOptionsSalesAgents ?? [],
    formOptionsTerms: formOptionsTerms ?? [],
    formOptionsPricingSchemes: formOptionsPricingSchemes ?? [],
    formOptionsSalesRegions: formOptionsSalesRegions ?? [],
    formOptionsAccountTypes: formOptionsAccountTypes ?? []
  };
}
